import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import FileUpload from '../../components/common/FileUpload';
import { employerAPI, utilityAPI } from '../../services/api';
import { toast } from 'sonner';
import { Building, Camera, Loader2, Save, Globe } from 'lucide-react';

const companySizes = [
  { value: '1-10', label: '1-10 employees' },
  { value: '11-50', label: '11-50 employees' },
  { value: '51-200', label: '51-200 employees' },
  { value: '201-1000', label: '201-1000 employees' },
  { value: '1000+', label: '1000+ employees' },
];

const Profile = ({ isFounderMode = false }) => {
  const [profile, setProfile] = useState(null);
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [profileRes, industriesRes] = await Promise.all([
        employerAPI.getProfile(),
        utilityAPI.getIndustries()
      ]);
      setProfile(profileRes.data);
      setIndustries(industriesRes.data);
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await employerAPI.updateProfile(profile);
      toast.success('Profile saved successfully!');
    } catch (error) {
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    const content = (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#1B5AFF]" />
      </div>
    );
    return isFounderMode ? content : <DashboardLayout>{content}</DashboardLayout>;
  }

  const profileContent = (
    <div className="max-w-3xl space-y-8" data-testid="employer-profile">
      {/* Header */}
      <div className="animate-fade-in">
        <h1 className="text-3xl font-bold text-[#0A0F1E]">Company Profile</h1>
        <p className="mt-2 text-[#5B6472]">Update your company information.</p>
      </div>

        {/* Company Logo */}
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in">
          <Label className="text-[#0A0F1E] mb-4 block">Company Logo</Label>
          <FileUpload
            value={profile?.logo_url || ''}
            onChange={(url) => handleChange('logo_url', url)}
            placeholder="Upload company logo"
          />
        </Card>

        {/* Company Info */}
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in">
          <h2 className="text-lg font-semibold text-[#0A0F1E] mb-6">Company Information</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="space-y-2 sm:col-span-2">
              <Label className="text-[#0A0F1E]">Company Name</Label>
              <Input
                value={profile?.company_name || ''}
                onChange={(e) => handleChange('company_name', e.target.value)}
                placeholder="Your Company Inc."
                className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-12"
                data-testid="profile-company-name"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#0A0F1E]">Industry</Label>
              <Select value={profile?.industry || ''} onValueChange={(v) => handleChange('industry', v)}>
                <SelectTrigger className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-12" data-testid="profile-industry">
                  <SelectValue placeholder="Select industry" />
                </SelectTrigger>
                <SelectContent className="bg-[#FFFFFF] border-[#E8EDF5]">
                  {industries.map(industry => (
                    <SelectItem key={industry} value={industry} className="text-[#0A0F1E] hover:bg-[#F5F8FF]">
                      {industry}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#0A0F1E]">Company Size</Label>
              <Select value={profile?.size || ''} onValueChange={(v) => handleChange('size', v)}>
                <SelectTrigger className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-12" data-testid="profile-size">
                  <SelectValue placeholder="Select size" />
                </SelectTrigger>
                <SelectContent className="bg-[#FFFFFF] border-[#E8EDF5]">
                  {companySizes.map(size => (
                    <SelectItem key={size.value} value={size.value} className="text-[#0A0F1E] hover:bg-[#F5F8FF]">
                      {size.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label className="text-[#0A0F1E]">Location</Label>
              <Input
                value={profile?.location || ''}
                onChange={(e) => handleChange('location', e.target.value)}
                placeholder="e.g., San Francisco, CA"
                className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-12"
                data-testid="profile-location"
              />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label className="text-[#0A0F1E]">Website</Label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#5B6472]" />
                <Input
                  value={profile?.website || ''}
                  onChange={(e) => handleChange('website', e.target.value)}
                  placeholder="https://yourcompany.com"
                  className="pl-10 bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-12"
                  data-testid="profile-website"
                />
              </div>
            </div>
          </div>
        </Card>

        {/* Description */}
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in">
          <div className="flex items-center justify-between mb-2">
            <Label className="text-[#0A0F1E]">Company Description</Label>
            <span className="text-[#5B6472] text-xs">{(profile?.description || '').length}/300</span>
          </div>
          <Textarea
            value={profile?.description || ''}
            onChange={(e) => handleChange('description', e.target.value.slice(0, 300))}
            placeholder="Tell candidates about your company..."
            className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl min-h-[120px]"
            data-testid="profile-description"
          />
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-xl px-8 h-12 btn-glow"
            data-testid="profile-save-btn"
          >
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save Profile
              </>
            )}
          </Button>
        </div>
      </div>
  );

  return isFounderMode ? profileContent : <DashboardLayout>{profileContent}</DashboardLayout>;
};

export default Profile;
